import React, { useCallback, useEffect, useState } from "react";
import { Button, Icon, Input, Modal, Popconfirm, message } from "antd";
import moment from "moment";
import { getData, createData } from "../../util/API";

const { TextArea } = Input;

const DISMISS_KEY = "partner_swap_banner_dismissed";
const POLL_MS = 5 * 60 * 1000;

/**
 * Global, dismissible banner shown to PMs/admins when wrong-partner
 * replacement requests are awaiting approval. Non-obstructive: collapses to a
 * slim strip, expandable per request, dismiss persists for the session.
 */
const PartnerSwapBanner = () => {
  const [swaps, setSwaps] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(
    sessionStorage.getItem(DISMISS_KEY) === "1"
  );
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const userRole = localStorage.getItem("user_role") || "";
  const isEligible = ["project_manager", "admin"].includes(userRole);

  const fetchSwaps = useCallback(() => {
    if (!isEligible) return;
    getData("/management/partner-swaps/?status=pending")
      .then((res) => {
        if (res.status === 200 && res.data) {
          setSwaps(res.data.swaps || []);
        }
      })
      .catch(() => {});
  }, [isEligible]);

  useEffect(() => {
    fetchSwaps();
    const timer = setInterval(fetchSwaps, POLL_MS);
    return () => clearInterval(timer);
  }, [fetchSwaps]);

  const performAction = useCallback(
    (swap, action, reason) => {
      setActionLoading(`${swap.id}-${action}`);
      createData("/management/partner-swap-action/", {
        swap_id: swap.id,
        action,
        rejected_reason: reason || undefined,
      })
        .then((res) => {
          if (res.data?.status === 200) {
            message.success(res.data.message);
            setRejectTarget(null);
            setRejectReason("");
            fetchSwaps();
          } else {
            message.error(res.data?.message || "Action failed");
          }
        })
        .catch((error) => {
          message.error("Error: " + error.toString());
        })
        .finally(() => setActionLoading(null));
    },
    [fetchSwaps]
  );

  if (!isEligible || dismissed || swaps.length === 0) {
    return null;
  }

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      style={{
        margin: "12px 16px 0",
        borderRadius: 12,
        border: "1px solid #f0d9bd",
        background: "linear-gradient(135deg, #fffaf3, #fdf1df)",
        boxShadow: "0 2px 12px rgba(180, 110, 30, 0.10)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
          onClick={() => setExpanded(!expanded)}
        >
          <span
            style={{
              minWidth: 26,
              height: 26,
              padding: "0 8px",
              borderRadius: 13,
              background: "#b4540a",
              color: "#fff",
              fontWeight: 700,
              fontSize: 13,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {swaps.length}
          </span>
          <span style={{ fontWeight: 700, color: "#6b3c08" }}>
            Wrong Partner replacement{swaps.length > 1 ? "s" : ""} awaiting your
            approval
          </span>
          <Icon
            type={expanded ? "up" : "down"}
            style={{ fontSize: 11, color: "#b4540a" }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <a
            onClick={() => setExpanded(!expanded)}
            style={{ fontSize: 12, color: "#b4540a", fontWeight: 600 }}
          >
            {expanded ? "Collapse" : "Review now"}
          </a>
          <Icon
            type="close"
            style={{ fontSize: 12, color: "#bb9a72", cursor: "pointer" }}
            onClick={dismiss}
          />
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "0 16px 14px" }}>
          {swaps.map((swap) => (
            <div
              key={swap.id}
              style={{
                background: "#fff",
                borderRadius: 10,
                border: "1px solid #f3e3cd",
                padding: "12px 16px",
                marginBottom: 10,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 14,
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: 1, minWidth: 280 }}>
                <div style={{ fontWeight: 700, color: "#33321f", marginBottom: 2 }}>
                  {swap.ticket_number}
                  <span
                    style={{
                      fontSize: 11,
                      color: "#9a9a9a",
                      marginLeft: 8,
                      fontWeight: 400,
                    }}
                  >
                    {swap.project_name}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: "#5d5337" }}>
                  <b>{swap.old_partner_name}</b>
                  <Icon
                    type="arrow-right"
                    style={{ margin: "0 6px", color: "#b4540a", fontSize: 11 }}
                  />
                  <b>{swap.new_partner_name}</b>
                </div>
                <div style={{ fontSize: 12, color: "#9a8a6a", marginTop: 2 }}>
                  WO {swap.work_order_number} · ₹{swap.work_order_amount} ·{" "}
                  {swap.work_order_status} · requested by {swap.requested_by}{" "}
                  {swap.requested_at
                    ? moment(swap.requested_at).fromNow()
                    : ""}
                </div>
                {swap.reason && (
                  <div style={{ fontSize: 12, color: "#75683f", marginTop: 4 }}>
                    Reason: {swap.reason}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  size="small"
                  onClick={() => setRejectTarget(swap)}
                  loading={actionLoading === `${swap.id}-reject`}
                >
                  Reject
                </Button>
                <Popconfirm
                  title={
                    <div style={{ maxWidth: 300 }}>
                      Replace <b>{swap.old_partner_name}</b> with{" "}
                      <b>{swap.new_partner_name}</b>? The WO, visit and actions
                      will be mirrored to the new partner.
                    </div>
                  }
                  okText="Approve & Replace"
                  onConfirm={() => performAction(swap, "approve")}
                >
                  <Button
                    size="small"
                    type="primary"
                    loading={actionLoading === `${swap.id}-approve`}
                  >
                    Approve
                  </Button>
                </Popconfirm>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        title="Reject Replacement Request"
        visible={!!rejectTarget}
        onCancel={() => {
          setRejectTarget(null);
          setRejectReason("");
        }}
        onOk={() => {
          if (!rejectReason.trim()) {
            message.error("Please add a short note for the coordinator.");
            return;
          }
          performAction(rejectTarget, "reject", rejectReason.trim());
        }}
        okText="Reject Request"
        okType="danger"
        confirmLoading={actionLoading === `${rejectTarget?.id}-reject`}
        destroyOnClose
      >
        <p style={{ fontSize: 13 }}>
          Rejecting keeps <b>{rejectTarget?.old_partner_name}</b> allocated on{" "}
          <b>{rejectTarget?.ticket_number}</b>. Nothing changes on the ticket.
        </p>
        <TextArea
          rows={3}
          value={rejectReason}
          placeholder="Why is this replacement rejected?"
          onChange={(event) => setRejectReason(event.target.value)}
        />
      </Modal>
    </div>
  );
};

export default PartnerSwapBanner;
